/**
 * The one shell every tenant-root page renders inside.
 *
 * Since the host consolidation the tenant's whole app lives on one root host
 * ({slug}.systemdox.com). This shell gives every page identical chrome: the one
 * canonical SystemDoxHeader and the BottomNav bar (the coarse quick-access) —
 * the shared navigation, rendered here and ONLY here, so it is the same on the
 * hub, the Add/Docs/Gaps pages and every console pillar. The routed page renders
 * through <Outlet/>.
 *
 * Providers: it mounts the light, always-on contexts every page's header needs —
 * Tenant and Project (both fail-soft) plus the notification providers (so the
 * bell works everywhere). The heavier WorkspaceProvider / PublishJobProvider are
 * NOT mounted here — they stay scoped to the routes that consume them (the
 * console pillars via ConsolePillars, and /docs), so the hub / Add / Gaps never
 * fire those workspace fetches.
 *
 * Layout: a single h-dvh flex column — header, then a bounded (min-h-0,
 * non-scrolling) <main>, then the BottomNav as the last child. <main> does NOT
 * scroll; each routed body owns its own scroll (a hub/Add/Gaps scroll wrapper,
 * or DocViewerPage's internal panes), which is what lets the Docs page's h-full
 * layout resolve. Because the BottomNav is a flex child (not fixed), nothing is
 * occluded and no spacer is needed.
 */

import { Outlet, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import {
  NotificationProvider,
  NotificationOverlayProvider,
  NotificationPanel,
  useNotificationOverlay,
} from '@puglieseweb/shared-react-notifications';
import { useAuthContext } from '@/contexts/AuthContext';
import { TenantProvider, useTenant } from '@/contexts/TenantContext';
import { ProjectProvider } from '@/contexts/ProjectContext';
import { BookmarksProvider } from '@/contexts/BookmarksContext';
import { SystemDoxHeader } from '@/components/layout/SystemDoxHeader';
import { BottomNav } from '@/components/layout/BottomNav';
import { EmaAssistant } from '@/components/ema/EmaAssistant';
import { tenantUrl, tenantHost } from '@/utils/subdomain';
import {
  systemdoxNotificationAdapter,
  notificationActionUrl,
} from '@/services/notificationAdapter';

/**
 * Warns when the signed-in tenant differs from the host being browsed (all data
 * shown belongs to the token's tenant, not the subdomain). useTenant is
 * fail-soft, so this is safe on every page.
 */
function TenantMismatchBanner() {
  const { tenant, mismatch, label } = useTenant();

  if (!mismatch || !tenant?.slug) return null;

  return (
    <div className="flex items-center justify-center gap-2 bg-amber-50 dark:bg-amber-900/40 border-b border-amber-200 dark:border-amber-800 px-4 py-2 text-sm text-amber-800 dark:text-amber-200">
      <AlertTriangle className="w-4 h-4 shrink-0" />
      <span>
        You are browsing <strong>{window.location.host}</strong> but signed in to{' '}
        <strong>{label}</strong> — all data shown belongs to {label}.
      </span>
      <a href={tenantUrl(tenant.slug)} className="font-medium underline hover:no-underline">
        Go to {tenantHost(tenant.slug)}
      </a>
    </div>
  );
}

function TenantShellInner() {
  const navigate = useNavigate();
  const { isOpen, toggle, close } = useNotificationOverlay();

  return (
    // text-foreground pairs with bg-background: descendants that set no colour
    // of their own (form controls especially — preflight gives them
    // `color: inherit`) would otherwise inherit the UA's black in both themes.
    <div className="h-dvh flex flex-col bg-background text-foreground">
      <header className="sticky top-0 z-10 flex shrink-0 items-center border-b border-border bg-card px-4 py-3">
        <SystemDoxHeader
          showSearch
          showNotifications
          onNotificationsClick={toggle}
          settingsTo="/settings"
          notificationsTo="/notifications"
        />
      </header>

      <TenantMismatchBanner />

      {/* Bounded, non-scrolling: routed bodies own their own scroll. */}
      <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <Outlet />
      </main>

      {/* The coarse quick-access bar — same bar on every page, all sizes. */}
      <BottomNav />

      {/* Ema, mounted here so she is reachable from every signed-in page.
          Fixed-position, so she sits outside the flex column and occludes
          nothing; she is raised clear of the BottomNav. */}
      <EmaAssistant />

      <NotificationPanel
        isOpen={isOpen}
        onClose={close}
        onNotificationClick={(notification) => {
          // Durable notifications carry their link in job.meta, not actionUrl.
          const url = notificationActionUrl(notification);
          if (url) navigate(url);
        }}
      />
    </div>
  );
}

export function TenantShell() {
  const { user } = useAuthContext();

  return (
    <NotificationProvider
      config={{
        adapter: systemdoxNotificationAdapter,
        isAuthenticated: !!user,
      }}
    >
      <NotificationOverlayProvider>
        <TenantProvider>
          <ProjectProvider>
            <BookmarksProvider>
              <TenantShellInner />
            </BookmarksProvider>
          </ProjectProvider>
        </TenantProvider>
      </NotificationOverlayProvider>
    </NotificationProvider>
  );
}
